import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';
import { UserSchema } from './user';

/**
 * Lectura ↔ partner LMS-gateway integráció — Zod sémák.
 *
 * Három felület:
 *   1. Eredmény-üzenet (Lectura → Gateway, Azure Service Bus)
 *   2. Launch context (Gateway → Lectura, aláírt URL/JWT)
 *   3. Integráció-katalógus (Gateway → Lectura, page-based REST)
 *
 * A partner felelőse: Repiczki Zoltán. A még nyitott szemantikai döntéseket
 * (lásd project_lms_integration memória) kommentek jelzik.
 */

// ===========================================================================
// 1. Eredmény-üzenet (ASB-re kiküldött)
// ===========================================================================

/**
 * A partner enum-ja. Zoli válasza alapján a Lectura "epub"-ot küld; a "scorm"
 * a legacy SCORM-lejátszók értéke, nálunk nem fordul elő, de a partner enum-jában
 * ott van — `parse` szempontból megengedjük (defenzív input).
 */
export const LmsServiceTypeSchema = z.enum(['epub', 'scorm']);
export type LmsServiceType = z.infer<typeof LmsServiceTypeSchema>;

/**
 * Az Azure Service Buson kiküldött eredmény-üzenet — Zoli 2026-05-05-i válasza
 * alapján rögzített.
 *
 * Idempotencia + összerendelés: a partner az `(contentId, resultId)` (esetleg
 * + `userId`) párral azonosít egy eredményt. A publisher az ASB MessageId-be
 * a `resultId`-t teszi, az ApplicationProperties.contentId-be a contentId-t.
 *
 * **Ne küldjünk** `satisfied` és `measure` mezőket — Zoli kérése alapján csak a
 * swagger kötelező mezői mennek. (A séma a kompozíció miatt ezeket sem
 * fogadja, hogy fordítási idejű hiba szóljon, ha valaki mégis beletenné.)
 *
 * `progress` 0..1 (NEM százalék). `totalTime` másodperc, kumulatív.
 */
export const LmsResultEventSchema = z
  .object({
    serviceType: LmsServiceTypeSchema,
    contentId: z.string().min(1).max(256),
    resultId: UuidSchema,
    userId: UuidSchema,
    completed: z.boolean(),
    progress: z.number().min(0).max(1),
    totalTime: z.number().int().nonnegative(),
    recordTime: IsoDateTimeSchema,
  })
  .strict();
export type LmsResultEvent = z.infer<typeof LmsResultEventSchema>;

// ===========================================================================
// Outbox / publisher státusz — admin UI nézet
// ===========================================================================

export const LmsResultDispatchStatusSchema = z.enum([
  'pending',
  'sending',
  'sent',
  'failed',
]);
export type LmsResultDispatchStatus = z.infer<typeof LmsResultDispatchStatusSchema>;

/**
 * Egy outbox-rekord életciklus-állapota a publisher pipeline-ban.
 * Az `attempt` a retry-számláló; a `lastError` a legutóbbi sikertelen küldés
 * üzenete (RFC 9457 detail-jellegű, max 2KB). A backend laposan adja vissza,
 * a payload mezők közvetlenül a rekordon szerepelnek (nem nested objektumként).
 */
export const LmsResultDispatchRecordSchema = z.object({
  id: UuidSchema,
  tenantId: z.string(),
  contentId: z.string(),
  resultId: UuidSchema,
  userId: UuidSchema,
  completed: z.boolean(),
  progress: z.number().min(0).max(1),
  totalTimeSeconds: z.number().int().nonnegative(),
  recordTime: IsoDateTimeSchema,
  status: LmsResultDispatchStatusSchema,
  attempt: z.number().int().nonnegative(),
  lastError: z.string().nullable(),
  enqueuedAt: IsoDateTimeSchema,
  sentAt: IsoDateTimeSchema.nullable(),
});
export type LmsResultDispatchRecord = z.infer<typeof LmsResultDispatchRecordSchema>;

export const LmsResultDispatchListSchema = z.object({
  data: z.array(LmsResultDispatchRecordSchema),
});
export type LmsResultDispatchList = z.infer<typeof LmsResultDispatchListSchema>;

/** Aggregált számlálók a Beállítások › Integrációk › Eredmények tabhoz. */
export const LmsResultDispatchSummarySchema = z.object({
  pending: z.number().int().nonnegative(),
  sending: z.number().int().nonnegative(),
  sentLast24h: z.number().int().nonnegative(),
  failedLast24h: z.number().int().nonnegative(),
});
export type LmsResultDispatchSummary = z.infer<typeof LmsResultDispatchSummarySchema>;

// ===========================================================================
// 2. Launch context (DRAFT — token-formátum partner-megerősítésre vár)
// ===========================================================================

/**
 * Két támogatott launch-flow:
 *
 *   - `backchannel-session`: a Gateway hív minket egy védett API-n
 *     (`POST /api/integration/launch/init`), megkapja a sessionId-t,
 *     az LMS csak `/launch?sid=<sessionId>`-t kap. Az URL-ben nincs PII,
 *     kulcs-rotáció a backchannel-auth cseréjével történik.
 *   - `hmac-query`: a Gateway megosztott kulccsal HMAC-SHA256-tel aláírt
 *     URL-t generál (LTI 1.0 stílus). Egyszerűbb, de PII az URL-ben.
 *
 * A választás üzemeltetési, nem kódbeli — mindkét folyamat ugyanazt a
 * `LmsLaunchContext`-et eredményezi a session-cache-ben.
 */
export const LmsLaunchFlowSchema = z.enum(['backchannel-session', 'hmac-query']);
export type LmsLaunchFlow = z.infer<typeof LmsLaunchFlowSchema>;

/**
 * Backchannel `POST /api/integration/launch/init` payload — a Gateway küldi.
 * A válasz `LmsLaunchInitResponse` (`sessionId` + `launchUrl`).
 */
export const LmsLaunchInitRequestSchema = z.object({
  userId: UuidSchema,
  contentId: z.string().min(1).max(256),
  resultId: UuidSchema,
  returnUrl: z.string().url(),
  remainingMinutes: z.number().int().positive().nullable(),
});
export type LmsLaunchInitRequest = z.infer<typeof LmsLaunchInitRequestSchema>;

export const LmsLaunchInitResponseSchema = z.object({
  sessionId: UuidSchema,
  launchUrl: z.string().url(),
  expiresAt: IsoDateTimeSchema,
});
export type LmsLaunchInitResponse = z.infer<typeof LmsLaunchInitResponseSchema>;

/**
 * A launch session kontextusa — Redisben él, a watchdog ezt figyeli.
 *
 * Élettartama: `remainingMinutes` perc, vagy a backchannel sessionInit
 * `expiresAt`-je — amelyik korábbi. Az `expiresAt` abszolút időbélyeg,
 * a reader-web és az integration-svc egyaránt ezt nézi.
 *
 * Composite key: a `(contentId, resultId)` egyértelműen azonosít egy
 * eredményt; a Redis-cache kulcsa `lectura:launch:active:{contentId}:{resultId}`,
 * második megnyitás → 409 Conflict / "már nyitott" oldal.
 */
export const LmsLaunchContextSchema = z.object({
  sessionId: UuidSchema,
  userId: UuidSchema,
  contentId: z.string().min(1).max(256),
  resultId: UuidSchema,
  returnUrl: z.string().url(),
  remainingMinutes: z.number().int().positive().nullable(),
  expiresAt: IsoDateTimeSchema,
  flow: LmsLaunchFlowSchema,
});
export type LmsLaunchContext = z.infer<typeof LmsLaunchContextSchema>;

/**
 * Egy launch-csere audit-rekord — a Beállítások › Integrációk › Tanuló
 * átküldés tab utolsó belépések listájához. Egy rekord egy sessionId-hez
 * tartozik, az `status` az utolsó ismert állapotot tükrözi (state-history-t
 * MVP-ben nem tartunk, csak az aktuális állapotot).
 */
export const LmsLaunchAuditStatusSchema = z.enum([
  'initialized',
  'consumed',
  'duplicate',
  'expired',
  'terminated',
  'error',
]);
export type LmsLaunchAuditStatus = z.infer<typeof LmsLaunchAuditStatusSchema>;

export const LmsLaunchAuditRecordSchema = z.object({
  id: UuidSchema,
  tenantId: z.string(),
  sessionId: UuidSchema,
  flow: LmsLaunchFlowSchema,
  status: LmsLaunchAuditStatusSchema,
  lecturaUserId: UuidSchema.nullable(),
  externalUserId: z.string(),
  contentId: z.string(),
  resultId: UuidSchema,
  returnUrl: z.string(),
  remainingMinutes: z.number().int().nullable(),
  expiresAt: IsoDateTimeSchema,
  errorMessage: z.string().nullable(),
  initializedAt: IsoDateTimeSchema,
  consumedAt: IsoDateTimeSchema.nullable(),
  terminatedAt: IsoDateTimeSchema.nullable(),
});
export type LmsLaunchAuditRecord = z.infer<typeof LmsLaunchAuditRecordSchema>;

export const LmsLaunchAuditListSchema = z.object({
  data: z.array(LmsLaunchAuditRecordSchema),
});
export type LmsLaunchAuditList = z.infer<typeof LmsLaunchAuditListSchema>;

/** Egy éppen-élő launch session — a Redis-cache snapshotja. */
export const LmsLaunchActiveSessionSchema = z.object({
  sessionId: UuidSchema,
  tenantId: z.string(),
  lecturaUserId: UuidSchema,
  externalUserId: UuidSchema,
  contentId: z.string(),
  resultId: UuidSchema,
  returnUrl: z.string(),
  remainingMinutes: z.number().int().nullable(),
  expiresAt: IsoDateTimeSchema,
  flow: LmsLaunchFlowSchema,
});
export type LmsLaunchActiveSession = z.infer<typeof LmsLaunchActiveSessionSchema>;

export const LmsLaunchActiveSessionListSchema = z.object({
  data: z.array(LmsLaunchActiveSessionSchema),
});
export type LmsLaunchActiveSessionList = z.infer<
  typeof LmsLaunchActiveSessionListSchema
>;

// ===========================================================================
// 3. Integráció-katalógus (DRAFT — részletek partner-egyeztetésre)
// ===========================================================================

/**
 * A gateway által várt katalógus-elem. Mezők szemantikája részben nyitott:
 *   - `languageId`: ISO 639-1 vagy 639-3 kód, esetleg GUID — egyeztetés alatt.
 *   - `pageCount`: reflowable EPUB-nál 0 vagy becsült — egyeztetés alatt.
 *   - `version`: publikáció-verzió vagy manifestáció-hash — egyeztetés alatt.
 *   - `organizationIds`: tenant vagy collection — egyeztetés alatt.
 *
 * Most a partner-séma legmegengedőbb formáját követjük; szigorítás később.
 */
export const IntegrationCatalogItemSchema = z.object({
  catalogId: z.string().min(1),
  title: z.string(),
  description: z.string().nullable().optional(),
  createdAt: IsoDateTimeSchema,
  lastModifiedAt: IsoDateTimeSchema,
  organizationIds: z.array(z.string()),
  author: z.string().nullable().optional(),
  isbn: z.string().nullable().optional(),
  version: z.string().nullable().optional(),
  languageId: z.string().nullable().optional(),
  pageCount: z.number().int().nonnegative().nullable().optional(),
});
export type IntegrationCatalogItem = z.infer<typeof IntegrationCatalogItemSchema>;

/** Page-based wrapper a partner katalógus-API formátuma szerint. */
export const IntegrationCatalogPageSchema = z.object({
  currentPage: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  pageSize: z.number().int().positive(),
  items: z.array(IntegrationCatalogItemSchema),
});
export type IntegrationCatalogPage = z.infer<typeof IntegrationCatalogPageSchema>;

// ===========================================================================
// External user lista — admin nézet
// ===========================================================================

/**
 * A `GET /api/identity/external-users` válasza — az admin Tanuló átküldés
 * tabján listázott LMS-ből provisionált fiókok.
 */
export const ExternalUserListSchema = z.object({
  data: z.array(UserSchema),
});
export type ExternalUserList = z.infer<typeof ExternalUserListSchema>;
